import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Building, Landmark, ShoppingBag, Handshake } from 'lucide-react';

export const LandAssetTransactions = () => {
  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-link">
          <ArrowLeft size={18} />
          Back to Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/services/real-estate" className="breadcrumb-link">Real Estate Advisory</Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Land & Asset Transactions</span>
      </div>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="service-detail-icon">
          <MapPin size={48} />
        </div>
        <h1 className="service-detail-title">Land & Asset Transactions</h1>
        <p className="service-detail-subtitle">
          Strategic advisory and transaction facilitation for land parcels, development opportunities and large-format real estate assets.
        </p>
      </section>

      {/* What We Do */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">What We Do</h2>
          <p className="service-section-text" style={{maxWidth: '900px', margin: '0 auto 3rem', textAlign: 'center'}}>
            HSNL Advisory facilitates acquisition, divestment and joint development opportunities across land and large-format real estate assets, connecting landowners, developers, investors and HNIs for suitable transactions.
          </p>

          {/* Three Main Service Blocks */}
          <div className="land-services-grid">
            {/* Land Sale & Acquisition */}
            <div className="land-service-block">
              <div className="service-block-icon">
                <MapPin size={36} />
              </div>
              <h3 className="service-block-title">Land Sale & Acquisition</h3>
              <p className="service-block-intro">
                We facilitate the buying and selling of land parcels for developers, investors, HNIs and other institutional or strategic buyers.
              </p>
              <ul className="service-block-list">
                <li>Large land parcels</li>
                <li>Developer land requirements</li>
                <li>Agricultural land</li>
                <li>Commercial land</li>
                <li>Development land</li>
                <li>Strategic land holdings</li>
                <li>Investment-oriented land opportunities</li>
              </ul>
            </div>

            {/* Joint Venture & Development Opportunities */}
            <div className="land-service-block">
              <div className="service-block-icon">
                <Handshake size={36} />
              </div>
              <h3 className="service-block-title">Joint Venture & Development Opportunities</h3>
              <p className="service-block-intro">
                We connect landowners with suitable developers and investors for structured development opportunities.
              </p>
              <ul className="service-block-list">
                <li>Joint Venture opportunities</li>
                <li>Joint Development arrangements</li>
                <li>Landowner–Developer collaborations</li>
                <li>Development partnerships</li>
                <li>Revenue-sharing structures</li>
                <li>Other mutually agreed development structures</li>
              </ul>
            </div>

            {/* Commercial & Large Asset Transactions */}
            <div className="land-service-block">
              <div className="service-block-icon">
                <Building size={36} />
              </div>
              <h3 className="service-block-title">Commercial & Large Asset Transactions</h3>
              <p className="service-block-intro">
                We facilitate acquisition and divestment opportunities involving standalone and large-format commercial real estate assets.
              </p>
              <ul className="service-block-list">
                <li>Standalone commercial buildings</li>
                <li>Shopping malls and retail assets</li>
                <li>Commercial land parcels</li>
                <li>Large investment properties</li>
                <li>Income-generating commercial assets</li>
                <li>Vacant or redevelopment-oriented commercial properties</li>
                <li>Large privately held real estate assets</li>
              </ul>
              <p className="service-block-closing">
                Positioned towards developers, HNIs, investors, family offices and institutional or strategic buyers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Asset Classes We Deal In */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Asset Classes We Deal In</h2>
          
          <div className="asset-classes-grid">
            <div className="asset-class-card">
              <div className="asset-icon">
                <MapPin size={32} />
              </div>
              <h3 className="asset-title">Large Land Parcels</h3>
              <p className="asset-description">
                Strategic land opportunities suitable for development, investment or long-term holding.
              </p>
            </div>

            <div className="asset-class-card">
              <div className="asset-icon">
                <Landmark size={32} />
              </div>
              <h3 className="asset-title">Agricultural Land</h3>
              <p className="asset-description">
                Large agricultural land opportunities subject to applicable local regulations and transaction requirements.
              </p>
            </div>

            <div className="asset-class-card">
              <div className="asset-icon">
                <Building size={32} />
              </div>
              <h3 className="asset-title">Commercial Land</h3>
              <p className="asset-description">
                Land suitable for commercial, mixed-use or other permissible development opportunities.
              </p>
            </div>

            <div className="asset-class-card">
              <div className="asset-icon">
                <Building size={32} />
              </div>
              <h3 className="asset-title">Standalone Commercial Buildings</h3>
              <p className="asset-description">
                Independent commercial properties available for acquisition, divestment or investment.
              </p>
            </div>

            <div className="asset-class-card">
              <div className="asset-icon">
                <ShoppingBag size={32} />
              </div>
              <h3 className="asset-title">Shopping Malls & Retail Assets</h3>
              <p className="asset-description">
                Existing and strategic retail real estate opportunities.
              </p>
            </div>

            <div className="asset-class-card">
              <div className="asset-icon">
                <Users size={32} />
              </div>
              <h3 className="asset-title">Development & JV Opportunities</h3>
              <p className="asset-description">
                Land parcels where owners are exploring collaboration with developers through JV, JDA, revenue sharing or other agreed structures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <div className="service-cta-box">
            <h2 className="cta-title">Discuss a Land or Asset Opportunity</h2>
            <p className="cta-text">
              Whether you are looking to acquire, divest or explore a development partnership for a land or commercial asset, connect with HSNL Advisory to discuss the opportunity.
            </p>
            <Link to="/#contact" className="btn-primary">
              Discuss an Opportunity
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
